/**
 * La file des scans d'une source, vue de l'écran (SCAN-QR § 3.3, 5 et § 5.2).
 *
 * Chaque scan est écrit dans la file du téléphone (`ouvrirFileDeScans` : IndexedDB, sinon
 * `localStorage`, sinon la mémoire) **avant tout envoi**, puis part par lots de 1 à 25
 * (`lotAEnvoyer`) ; la réponse de la base (`apresReponse`) retire les scans au résultat
 * définitif et laisse les autres (« code requis », « non traité », réseau coupé) avec un
 * prochain essai décalé. On envoie à chaque ajout, au retour du réseau (`online`), au retour
 * sur la page (`visibilitychange`) et sur minuterie ; un seul expéditeur par source, même avec
 * deux onglets ouverts (`navigator.locks` quand le navigateur l'a : l'onglet qui tient le verrou
 * relit la file commune à chaque tour, et envoie donc aussi les scans de l'autre).
 *
 * L'écart de l'horloge du téléphone au serveur se mesure sur chaque lecture de la liste
 * (`horloge`) et chaque réponse à un lot (`maintenant`). Le hook vit dans l'écran de pointage,
 * pas dans la feuille de scan : la file continue de partir quand la feuille est fermée.
 *
 * Le code du lien est précieux : chaque code faux compte (pause au 5e d'affilée, lien bloqué au
 * 30e sur sa vie, migration `20261103120000`). La file ne part donc qu'une fois la liste lue
 * avec cette source (le code vérifié par `garderie_lien_lire`), et après un « code requis » elle
 * ne repart plus avec la même source — ni au retour du réseau, ni au retour sur la page : il faut
 * une source neuve (le code retapé). PROPOSITION TECHNIQUE.
 *
 * Un lien renouvelé (le précédent révoqué), expiré ou bloqué ne reprend jamais ses scans : la
 * file arrête de renvoyer par lui (`lienMort`) et les garde. À chaque lecture réussie de la
 * liste, le téléphone reprend sous la source ouverte les scans d'un ancien lien de la même
 * école (`repartirLesAutresSources`) et les envoie ; les autres (une autre école, Gestion)
 * restent comptés dans `orphelines`, montrés en permanence hors de la feuille de scan. Une file
 * que le stockage ne sait plus relire (`illisible`) ne se dit jamais « 0 en attente ».
 * PROPOSITION TECHNIQUE.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { estErreurDeReseau, estRefusDefinitifDuLien, messageDErreur, type LecturePointage, type SourceDuPointage } from '../../../lib/garderie/pointage';
import { fileEnMemoire, ouvrirFileDeScans, type FileDeScans } from '../../../lib/garderie/file-de-scans';
import {
  apresReponse,
  ecartAuServeur,
  instantDeLaBase,
  lireEtiquette,
  lotAEnvoyer,
  ordreDeLaFile,
  repartirLesAutresSources,
  type EntreeDeFile,
  type ReponseDuScan,
  type ResultatDuScan,
} from '../../../lib/garderie/scan';
import { compterLaSession, dixDerniers, type Retour, type ScanDeLaSession } from './retours';

/** L'heure du serveur lue sur une réponse, avec l'heure du téléphone à l'envoi et à la réception. */
export interface MesureDHorloge {
  maintenant: string;
  envoyeLe: number;
  recuLe: number;
}

export interface EcartRetenu {
  ecartMs: number;
  allerRetourMs: number;
  /** `Date.now()` de la mesure. */
  le: number;
}

/** Au-delà de cet aller-retour, la mesure est trop floue pour corriger l'horloge. */
export const ALLER_RETOUR_MAX_MS = 5000;

/**
 * L'écart à retenir après une nouvelle mesure : la nouvelle, si son aller-retour est
 * raisonnable et pas nettement plus lent que celui de la mesure retenue (une mesure de plus de
 * 5 minutes cède toujours la place) ; sinon la précédente.
 */
export function retenirLEcart(precedent: EcartRetenu | null, mesure: MesureDHorloge): EcartRetenu | null {
  const allerRetourMs = mesure.recuLe - mesure.envoyeLe;
  if (!Number.isFinite(allerRetourMs) || allerRetourMs < 0 || allerRetourMs > ALLER_RETOUR_MAX_MS) return precedent;
  if (!Number.isFinite(instantDeLaBase(mesure.maintenant))) return precedent;
  const nouveau: EcartRetenu = { ecartMs: ecartAuServeur(mesure.maintenant, mesure.envoyeLe, mesure.recuLe), allerRetourMs, le: mesure.recuLe };
  if (!precedent) return nouveau;
  const vieux = mesure.recuLe - precedent.le > 5 * 60_000;
  return vieux || allerRetourMs <= precedent.allerRetourMs * 2 + 200 ? nouveau : precedent;
}

/** La file doublée d'un secours : elle sait dire si le stockage principal a pu être relu. */
export interface FileAvecSecours extends FileDeScans {
  /**
   * Faux quand la dernière lecture du stockage principal a échoué : les scans qu'il garde ne
   * sont alors pas comptés (seuls ceux du secours le sont), et l'écran ne doit pas dire
   * « 0 en attente ».
   */
  readonly lisible: boolean;
}

/**
 * La file, doublée d'une file en mémoire : si le stockage refuse une écriture (plein, effacé,
 * IndexedDB fermée par le navigateur et pas rouverte), le scan est gardé en mémoire et la file
 * se dit « non persistante » — l'écran le montre — plutôt que de perdre le scan. Une lecture
 * qui échoue rend ce que le secours garde et met `lisible` à faux.
 */
export function avecSecours(primaire: FileDeScans, secours: FileDeScans = fileEnMemoire()): FileAvecSecours {
  let degradee = false;
  let lisible = true;
  const unir = (a: EntreeDeFile[], b: EntreeDeFile[]) => {
    const parCle = new Map<string, EntreeDeFile>();
    for (const e of [...a, ...b]) parCle.set(e.cle, e);
    return ordreDeLaFile([...parCle.values()]);
  };
  return {
    get persistante() {
      return primaire.persistante && !degradee;
    },
    get support() {
      return primaire.support;
    },
    get lisible() {
      return lisible;
    },
    async toutes(source) {
      let a: EntreeDeFile[] = [];
      try {
        a = await primaire.toutes(source);
        lisible = true;
      } catch {
        degradee = true;
        lisible = false;
      }
      return unir(a, await secours.toutes(source));
    },
    async toutesLesSources() {
      let a: EntreeDeFile[] = [];
      try {
        a = await primaire.toutesLesSources();
        lisible = true;
      } catch {
        degradee = true;
        lisible = false;
      }
      return unir(a, await secours.toutesLesSources());
    },
    async ajouter(e) {
      try {
        await primaire.ajouter(e);
        await secours.retirer([e.cle]);
      } catch {
        degradee = true;
        await secours.ajouter(e);
      }
    },
    async remplacer(source, entrees) {
      try {
        await primaire.remplacer(source, entrees);
        await secours.remplacer(source, []);
      } catch {
        degradee = true;
        await secours.remplacer(source, entrees);
      }
    },
    async retirer(cles) {
      try {
        await primaire.retirer(cles);
      } catch {
        degradee = true;
      }
      await secours.retirer(cles);
    },
  };
}

export interface OptionsDeLaFile {
  /** La mesure d'horloge de la dernière lecture de la liste. */
  horloge?: MesureDHorloge | null;
  /** Après chaque lot qui a rendu des résultats définitifs (la liste se relit). */
  onLot?: (traites: ResultatDuScan[]) => void;
}

export interface FileDeLEcran {
  /** Écrit le scan dans la file, puis l'envoie. Faux si le téléphone n'a pas pu le garder. */
  ajouter(e: EntreeDeFile, apercu?: Retour | null): Promise<boolean>;
  /** Les scans de la source qui attendent sur le téléphone. */
  enAttente: number;
  /** Les scans de la session que la base a dits présents (hors annulés). */
  pointes: number;
  /** Les scans de la session refusés ou « à vérifier ». */
  aRevoir: number;
  /** Les dix derniers scans de la session, le plus récent d'abord. */
  derniers: ScanDeLaSession[];
  /** Tous les scans de la session. */
  session: ScanDeLaSession[];
  /** La base demande le code : les scans attendent. */
  codeRequis: boolean;
  /** `null` tant que la file s'ouvre ; faux si elle ne survivra pas à la page. */
  persistante: boolean | null;
  support: FileDeScans['support'] | null;
  /** L'écart de l'horloge du téléphone au serveur (ms ; positif quand le téléphone retarde). */
  ecartMs: number;
  /** Le dernier refus de la base à l'envoi (lien révoqué…), hors réseau ; `null` sinon. */
  erreur: string | null;
  /** Le lien est mort (remplacé, expiré, bloqué, inconnu) : la file ne repart plus par lui ; ses scans restent sur le téléphone. */
  lienMort: boolean;
  /** Les scans d'une autre source (autre école, Gestion) qui attendent sur ce téléphone sans pouvoir partir d'ici. */
  orphelines: EntreeDeFile[];
  /** Faux quand le stockage n'a pas pu être relu : `enAttente` peut alors être en dessous de la vérité. */
  lisible: boolean;
  /** Envoie maintenant ; `force` ignore les délais entre essais (retour du réseau, code retapé). */
  envoyer(force?: boolean): void;
}

type AvecVerrous = Navigator & {
  locks?: { request(nom: string, options: { ifAvailable: boolean }, rappel: (verrou: unknown) => Promise<void>): Promise<void> };
};

const PERIODE_MS = 3000;
const TOURS_MAX = 40;

export function useFileDeScans(source: SourceDuPointage, lecture: LecturePointage | null, options: OptionsDeLaFile = {}): FileDeLEcran {
  const cle = source.cle;
  const peutScanner = !!source.scanner;
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const onLotRef = useRef(options.onLot);
  onLotRef.current = options.onLot;

  const fileRef = useRef<Promise<FileAvecSecours> | null>(null);
  const [persistante, setPersistante] = useState<boolean | null>(null);
  const [support, setSupport] = useState<FileDeScans['support'] | null>(null);
  const [session, setSession] = useState<ScanDeLaSession[]>([]);
  const [enAttente, setEnAttente] = useState(0);
  const [codeRequis, setCodeRequis] = useState(false);
  /* La source qui a reçu « code requis » : la file ne repart plus par elle. */
  const codeRequisDe = useRef<SourceDuPointage | null>(null);
  /* La source dont le lien est mort : la file ne repart plus par elle. */
  const lienMortDe = useRef<SourceDuPointage | null>(null);
  const [lienMort, setLienMort] = useState(false);
  const [orphelines, setOrphelines] = useState<EntreeDeFile[]>([]);
  const [lisible, setLisible] = useState(true);
  /* L'école de la liste lue : celle des scans écrits, et celle dont on reprend les scans d'un ancien lien. */
  const ecole = lecture?.ecole.id ?? null;
  /* La liste a été lue au moins une fois : le code de la source est bon. */
  const lectureLue = lecture !== null;
  const lectureLueRef = useRef(lectureLue);
  lectureLueRef.current = lectureLue;
  const [erreur, setErreur] = useState<string | null>(null);
  const ecartRef = useRef<EcartRetenu | null>(null);
  const [ecartMs, setEcartMs] = useState(0);
  const enCours = useRef(false);
  const aRelancer = useRef<{ force: boolean } | null>(null);
  const enAttenteRef = useRef(0);
  const monte = useRef(true);

  const noterHorloge = useCallback((m: MesureDHorloge) => {
    const r = retenirLEcart(ecartRef.current, m);
    if (r && r !== ecartRef.current) {
      ecartRef.current = r;
      setEcartMs(r.ecartMs);
    }
  }, []);

  /* L'horloge de chaque lecture de la liste ; à défaut de mesure, l'heure du serveur de la lecture. */
  useEffect(() => {
    if (options.horloge) noterHorloge(options.horloge);
    else if (lecture?.maintenant) {
      const t = Date.now();
      noterHorloge({ maintenant: lecture.maintenant, envoyeLe: t, recuLe: t });
    }
  }, [options.horloge, lecture?.maintenant, noterHorloge]);

  const recompter = useCallback(async () => {
    const f = fileRef.current;
    if (!f) return;
    try {
      const fa = await f;
      const tout = await fa.toutesLesSources();
      const n = tout.filter((e) => e.source === cle).length;
      enAttenteRef.current = n;
      if (monte.current) {
        setEnAttente(n);
        setOrphelines(tout.filter((e) => e.source !== cle));
        setLisible(fa.lisible);
      }
    } catch {
      /* la file se relira au prochain tour */
    }
  }, [cle]);

  const envoyerRef = useRef<(force?: boolean) => void>(() => undefined);

  const envoyer = useCallback(
    (force = false) => {
      if (!peutScanner || !fileRef.current || !lectureLueRef.current) return;
      if (enCours.current) {
        aRelancer.current = { force: force || (aRelancer.current?.force ?? false) };
        return;
      }
      enCours.current = true;

      const travail = async () => {
        const f = await fileRef.current!;
        let forcer = force;
        for (let tour = 0; tour < TOURS_MAX && monte.current; tour += 1) {
          const src = sourceRef.current;
          if (src.cle !== cle || !src.scanner || codeRequisDe.current === src || lienMortDe.current === src) break;
          const toutes = await f.toutes(cle);
          const lot = lotAEnvoyer(toutes, forcer ? Number.POSITIVE_INFINITY : Date.now());
          if (lot.length === 0) break;
          forcer = false;

          const envoyeLe = Date.now();
          let reponse: ReponseDuScan | 'echec';
          try {
            reponse = await src.scanner(lot);
          } catch (e) {
            reponse = 'echec';
            if (monte.current) setErreur(estErreurDeReseau(e) ? null : messageDErreur(e));
            if (estRefusDefinitifDuLien(e)) {
              /* Le lien ne reprendra pas : on arrête d'envoyer par lui ; les scans restent, le nouveau lien les reprendra. */
              lienMortDe.current = src;
              if (monte.current) setLienMort(true);
            }
          }
          const recuLe = Date.now();
          if (reponse !== 'echec') {
            if (reponse.maintenant) noterHorloge({ maintenant: reponse.maintenant, envoyeLe, recuLe });
            if (monte.current) setErreur(null);
            if (lienMortDe.current !== null) {
              lienMortDe.current = null;
              if (monte.current) setLienMort(false);
            }
            if (!reponse.codeRequis && codeRequisDe.current !== null) {
              codeRequisDe.current = null;
              if (monte.current) setCodeRequis(false);
            }
          }

          const { file: apres, traites } = apresReponse(toutes, lot, reponse, recuLe);
          /* Écrire sans écraser un scan ajouté pendant l'envoi : retirer les traités, remettre les autres du lot. */
          const traitesCles = new Set(traites.map((t) => t.cle));
          await f.retirer([...traitesCles].filter((c): c is string => !!c));
          const duLot = new Set(lot.map((e) => e.cle));
          for (const e of apres) if (duLot.has(e.cle) && !traitesCles.has(e.cle)) await f.ajouter(e);

          if (traites.length > 0 && monte.current) {
            setSession((avant) => {
              const parCle = new Map(traites.map((t) => [t.cle, t]));
              const connues = new Set(avant.map((s) => s.cle));
              const maj = avant.map((s) => (parCle.has(s.cle) ? { ...s, resultat: parCle.get(s.cle)! } : s));
              const nouvelles = lot
                .filter((e) => parCle.has(e.cle) && !connues.has(e.cle))
                .map((e) => ({ ...sessionDeLEntree(e, null), resultat: parCle.get(e.cle)! }));
              return [...maj, ...nouvelles];
            });
            onLotRef.current?.(traites);
          }
          if (reponse === 'echec') break;
          if (reponse.codeRequis) {
            codeRequisDe.current = src;
            if (monte.current) setCodeRequis(true);
            break;
          }
          if (traites.length === 0) break; // rien de définitif : la base est occupée, on attend le délai
        }
      };

      const verrous = typeof navigator !== 'undefined' ? (navigator as AvecVerrous).locks : undefined;
      const tache = verrous
        ? verrous.request(`cdv-garderie-scans:${cle}`, { ifAvailable: true }, async (verrou) => {
            if (verrou) await travail();
          })
        : travail();
      void Promise.resolve(tache)
        .catch(() => undefined)
        .finally(() => {
          enCours.current = false;
          void recompter();
          const relance = aRelancer.current;
          aRelancer.current = null;
          if (relance && monte.current) envoyerRef.current(relance.force);
        });
    },
    [cle, peutScanner, noterHorloge, recompter],
  );
  envoyerRef.current = envoyer;

  /* Ouvrir la file, relire ce qui attend (un onglet fermé, un téléphone redémarré), l'envoyer. */
  useEffect(() => {
    monte.current = true;
    if (!peutScanner) return;
    let annule = false;
    const p = ouvrirFileDeScans().then((f) => avecSecours(f));
    fileRef.current = p;
    setSession([]);
    void p.then(async (f) => {
      if (annule) return;
      setPersistante(f.persistante);
      setSupport(f.support);
      try {
        const tout = await f.toutesLesSources();
        if (annule) return;
        const restantes = tout.filter((e) => e.source === cle);
        enAttenteRef.current = restantes.length;
        setEnAttente(restantes.length);
        setOrphelines(tout.filter((e) => e.source !== cle));
        setLisible(f.lisible);
        setSession(restantes.filter((e) => e.mode !== 'annulation').map((e) => sessionDeLEntree(e, null)));
      } catch {
        /* rien à relire */
      }
      envoyerRef.current(true);
    });
    return () => {
      annule = true;
      monte.current = false;
    };
  }, [cle, peutScanner]);

  /* La première lecture réussie de la liste (le code est bon) : ce qui attendait part. */
  useEffect(() => {
    if (lectureLue) envoyerRef.current(true);
  }, [lectureLue]);

  /*
    Chaque lecture réussie de la liste (une nouvelle heure du serveur) : le lien est vivant et son
    code bon. Les scans d'un ancien lien de la même école sont repris sous celui-ci et partent ;
    un onglet resté sur un lien mort ne lit plus rien, il ne reprend donc rien.
  */
  const lueA = lecture?.maintenant ?? null;
  useEffect(() => {
    const p = fileRef.current;
    if (!peutScanner || !p || !lectureLue || !ecole) return;
    let annule = false;
    void (async () => {
      try {
        const f = await p;
        const { aReprendre } = repartirLesAutresSources(await f.toutesLesSources(), cle, ecole);
        if (annule || aReprendre.length === 0) return;
        for (const e of aReprendre) await f.ajouter(e);
        if (annule) return;
        setSession((avant) => [
          ...avant,
          ...aReprendre.filter((e) => e.mode !== 'annulation' && !avant.some((s) => s.cle === e.cle)).map((e) => sessionDeLEntree(e, null)),
        ]);
        await recompter();
        envoyerRef.current(true);
      } catch {
        /* la prochaine lecture réessaiera */
      }
    })();
    return () => {
      annule = true;
    };
  }, [lueA, lectureLue, ecole, cle, peutScanner, recompter]);

  /* Au retour du réseau, au retour sur la page, et sur minuterie. */
  useEffect(() => {
    if (!peutScanner) return;
    const enLigne = () => envoyerRef.current(true);
    const auRetour = () => {
      if (document.visibilityState === 'visible') envoyerRef.current(true);
    };
    window.addEventListener('online', enLigne);
    document.addEventListener('visibilitychange', auRetour);
    const minuterie = window.setInterval(() => {
      if (enAttenteRef.current > 0) envoyerRef.current(false);
    }, PERIODE_MS);
    return () => {
      window.removeEventListener('online', enLigne);
      document.removeEventListener('visibilitychange', auRetour);
      window.clearInterval(minuterie);
    };
  }, [peutScanner]);

  const ajouter = useCallback(
    async (e: EntreeDeFile, apercu: Retour | null = null): Promise<boolean> => {
      const p = fileRef.current;
      if (!p) return false;
      try {
        const f = await p;
        await f.ajouter(e);
        setPersistante(f.persistante);
      } catch {
        return false;
      }
      setSession((avant) => [...avant.filter((s) => s.cle !== e.cle), sessionDeLEntree(e, apercu)]);
      enAttenteRef.current += 1;
      setEnAttente((n) => n + 1);
      envoyerRef.current(false);
      return true;
    },
    [],
  );

  const compte = useMemo(() => compterLaSession(session, enAttente), [session, enAttente]);
  const derniers = useMemo(() => dixDerniers(session), [session]);

  return {
    ajouter,
    enAttente: compte.enAttente,
    pointes: compte.pointes,
    aRevoir: compte.aRevoir,
    derniers,
    session,
    codeRequis,
    persistante,
    support,
    ecartMs,
    erreur,
    lienMort,
    orphelines,
    lisible,
    envoyer,
  };
}

/** Un scan de la file, tel que la session le garde (le moment : celui du téléphone). */
export function sessionDeLEntree(e: EntreeDeFile, apercu: Retour | null): ScanDeLaSession {
  const t = Date.parse(e.momentAppareil);
  return {
    cle: e.cle,
    mode: e.mode,
    annuleCle: e.annuleCle,
    qr: e.qr,
    enfantId: e.enfantId,
    plageId: e.plageId,
    moment: Number.isFinite(t) ? t : 0,
    apercu,
    resultat: null,
  };
}

/** Le numéro QuickSchool d'un QR gardé dans la file (lu strictement), ou `null`. */
export const numeroDuQr = (qr: string | null): string | null => (qr ? lireEtiquette(qr) : null);
